/**
 * Arranging the texts selected in My Texts on shelves: a sheet (ui/Sheet)
 * listing the shelves, each ticked if all the texts are on it (a dash if some
 * are), from which a new shelf can be made too. Its changes can be undone
 * while it's open.
 */
import { effect } from "@preact/signals";
import * as preact from "preact";

import * as premiumUpsellModal from "premiumUpsellModal";
import SelectionStore from "stores/SelectionStore";
import {
  getBookIndex,
  getValidBooks,
  ShelfBook,
  ShelfData,
} from "stores/ShelvesStore";
import stores from "stores/stores";
import Button from "ui/Button";
import Sheet, { showSheet } from "ui/Sheet";

type ShelfState = "checked" | "unchecked" | "indeterminate";

const getShelfState = (shelf: ShelfData, itemIDs: string[]): ShelfState => {
  const index = getBookIndex(shelf);
  const onShelf = itemIDs.filter((bookID) => index[bookID]).length;
  return onShelf === 0
    ? "unchecked"
    : onShelf === itemIDs.length
      ? "checked"
      : "indeterminate";
};

interface Props {
  model: SelectionStore;
}

interface State {
  addingShelf: boolean;
}

class ShelvesSheet extends preact.Component<Props, State> {
  state: State = { addingShelf: false };
  /** Each shelf's texts as it opened, to undo the changes made since */
  private readonly opened = new Map<string, ShelfBook[]>(
    stores.shelves.shelves.value.map((shelf) => [shelf._id, [...shelf.books]])
  );
  private newShelfInputRef = preact.createRef<HTMLInputElement>();
  private disposeEffect?: () => void;

  componentDidMount() {
    this.disposeEffect = effect(() => {
      void stores.shelves.shelves.value;
      void this.props.model.itemIDs.value;
      this.forceUpdate();
    });
  }

  componentWillUnmount() {
    this.disposeEffect?.();
  }

  private changed() {
    return stores.shelves.shelves.value.some(
      (shelf) => this.opened.get(shelf._id)?.length !== shelf.books.length
    );
  }

  private toggleShelf(shelfID: string, onShelf: boolean) {
    const shelf = stores.shelves.get(shelfID);
    if (!shelf) return;
    const selectedIDs = this.props.model.itemIDs.value;
    const selected = new Set(selectedIDs);
    const bookIDs = getValidBooks(shelf).map((book) => book.bookID);
    const nextBookIDs = onShelf
      ? Array.from(new Set([...bookIDs, ...selectedIDs]))
      : bookIDs.filter((bookID) => !selected.has(bookID));
    void stores.shelves.patchShelf(shelfID, {
      books: nextBookIDs.map((bookID) => ({ bookID })),
    });
  }

  private undoChanges = () => {
    for (const shelf of stores.shelves.shelves.value) {
      const opened = this.opened.get(shelf._id);
      if (!opened) {
        void stores.shelves.destroyShelf(shelf._id);
      } else if (opened.length !== shelf.books.length) {
        void stores.shelves.patchShelf(shelf._id, { books: opened });
      }
    }
  };

  private startAddingShelf = () => {
    this.setState({ addingShelf: true }, () => {
      this.newShelfInputRef.current?.focus();
    });
  };

  private createShelf = (event: Event) => {
    event.preventDefault();
    const name = this.newShelfInputRef.current?.value.trim() ?? "";
    this.setState({ addingShelf: false });
    if (name) {
      void stores.shelves.createShelf({
        name,
        language: stores.user.currentLearningLanguage.value,
        books: [],
      });
    }
  };

  render() {
    const itemIDs = this.props.model.itemIDs.value;
    const shelves = stores.shelves.shelves.value.filter(
      (shelf) => shelf._id !== "unshelved"
    );
    return (
      <Sheet
        title="Shelves"
        leading={
          this.changed()
            ? { label: "Undo", onClick: this.undoChanges }
            : undefined
        }
      >
        {shelves.length > 0 ? (
          <div class="shelfChoices">
            {shelves.map((shelf) => {
              const state = getShelfState(shelf, itemIDs);
              return (
                <label class="shelfChoice" key={shelf._id}>
                  <input
                    type="checkbox"
                    checked={state === "checked"}
                    indeterminate={state === "indeterminate"}
                    onClick={(event) =>
                      this.toggleShelf(shelf._id, event.currentTarget.checked)
                    }
                  />
                  <span class="shelfName">{shelf.name}</span>
                  <small>{shelf.bookCount ?? shelf.books.length}</small>
                </label>
              );
            })}
          </div>
        ) : null}
        {this.state.addingShelf ? (
          <form class="newShelfForm" onSubmit={this.createShelf}>
            <input
              type="text"
              class="input large newShelfInput"
              placeholder="Shelf name"
              aria-label="Shelf name"
              ref={this.newShelfInputRef}
            />
            <Button kind="primary" size="large" type="submit">
              Create
            </Button>
          </form>
        ) : (
          <Button
            kind="plain"
            class="addNewShelf"
            icon="icon-plus"
            onClick={this.startAddingShelf}
          >
            New Shelf…
          </Button>
        )}
      </Sheet>
    );
  }
}

/**
 * Opens the shelves for the texts selected (from My Texts' Shelves… button
 * or Actions menu), or Premium's upsell; `onClose` as it closes
 */
export const showShelvesSheet = (
  model: SelectionStore,
  onClose: () => void
) => {
  const plan = stores.user.state.value.subscriptionPlan;
  if (plan !== "standard" && plan !== "plus" && plan !== "freeForever") {
    premiumUpsellModal.show({
      model: stores.user,
      title: "shelves",
      heading: "Go premium to create shelves...",
      showTitle: false,
    });
    return;
  }
  showSheet(<ShelvesSheet model={model} />, { cancelCallback: onClose });
};

export const _functionsForUnitTests = { getShelfState };
